import { posthog, PostHogConfig } from "posthog-js";

import { getCurrentPreferences } from "../../modules/CookieConsent/utils/consentManager";
import { BUILD_ENV } from "../../shared/constants/environment";
import { shouldSendAnalyticsCall } from "./analyticsGate";

const POSTHOG_API_HOST = "https://us.i.posthog.com";
const POSTHOG_UI_HOST = "https://us.posthog.com";
const POSTHOG_RECORDING_HOSTS_BY_ENV: Record<string, Set<string>> = {
  integration: new Set(["integration-my-v3.wingspan.app"]),
  production: new Set(["my-v3.wingspan.app"]),
  staging: new Set(["staging-my-v3.wingspan.app"])
};

export interface PostHogEligibility {
  analyticsConsent: boolean;
  buildEnv?: string;
  hostname: string;
  projectToken?: string;
  shouldSendAnalytics: boolean;
}

export const isPostHogEligible = ({
  analyticsConsent,
  buildEnv,
  hostname,
  projectToken,
  shouldSendAnalytics
}: PostHogEligibility): boolean =>
  !!projectToken?.match(/^phc_[A-Za-z0-9]+$/) &&
  analyticsConsent &&
  shouldSendAnalytics &&
  !!buildEnv &&
  !!POSTHOG_RECORDING_HOSTS_BY_ENV[buildEnv]?.has(hostname);

export const getPostHogConfig = (): Partial<PostHogConfig> => ({
  api_host: POSTHOG_API_HOST,
  ui_host: POSTHOG_UI_HOST,
  defaults: "2026-06-25",
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_session_recording: true,
  enable_recording_console_log: true,
  person_profiles: "identified_only",
  session_recording: {
    blockSelector: ".ph-no-capture",
    maskAllInputs: true
  },
  integrations: {
    intercom: true,
    crispChat: false
  }
});

class PostHogService {
  private isInitialized = false;
  private isRecording = false;
  private identifiedAccountId: string | undefined;

  load(): void {
    if (this.isInitialized) {
      return;
    }

    const projectToken = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
    const { C0002AL: analyticsConsent } = getCurrentPreferences();
    const isEligible = isPostHogEligible({
      analyticsConsent,
      buildEnv: BUILD_ENV,
      hostname: window.location.hostname,
      projectToken,
      shouldSendAnalytics: shouldSendAnalyticsCall()
    });

    if (!isEligible || !projectToken) {
      return;
    }

    try {
      posthog.init(projectToken, getPostHogConfig());
      this.isInitialized = true;
    } catch {
      console.error("Failed to initialize PostHog");
    }
  }

  identify(accountId?: string): void {
    if (!this.isInitialized || !accountId || !shouldSendAnalyticsCall()) {
      return;
    }

    if (this.identifiedAccountId === accountId) {
      return;
    }

    if (this.identifiedAccountId) {
      posthog.stopSessionRecording();
      posthog.reset();
      this.isRecording = false;
    }

    posthog.identify(accountId);
    posthog.startSessionRecording();
    this.identifiedAccountId = accountId;
    this.isRecording = true;
  }

  syncSessionReplay({ accountId }: { accountId?: string }): void {
    this.load();
    this.identify(accountId);
  }

  reset(): void {
    if (!this.isInitialized) {
      return;
    }

    posthog.stopSessionRecording();
    posthog.reset();
    this.identifiedAccountId = undefined;
    this.isRecording = false;
  }

  getSessionReplayUrl(): string {
    if (!this.isRecording) {
      return "";
    }

    return posthog.get_session_replay_url({
      withTimestamp: true,
      timestampLookBack: 30
    });
  }
}

export const postHogService = new PostHogService();
